import type { Ref } from "react";
import { cn } from "@/lib/utils";

/**
 * On-screen diameter of the dot in px at `--pin-scale: 1`. Matches the
 * plan-viewer ghost pin (the canonical placement marker) so every surface
 * shows the same dot. Rides `--pin-scale`, so this is its size at native zoom.
 */
const DEFAULT_SIZE_PX = 16;

export interface PinMarkerDotProps {
  /** Pin location in image-relative coords `[0,1]` — the dot centres here. */
  xPct: number;
  yPct: number;
  /** On-screen diameter in px at `--pin-scale: 1`. Defaults to {@link DEFAULT_SIZE_PX}. */
  size?: number;
  /**
   * Ride the plan's inverse-zoom `--pin-scale` so the dot holds a constant
   * on-screen size while the plan zooms. True on a zoomable plan surface;
   * false on a static thumbnail (no `--pin-scale` in scope). Defaults to true.
   */
  scaleWithZoom?: boolean;
  /** Escape hatch for extra classes (rarely needed). */
  className?: string;
  /**
   * Forwarded to the dot element. The plan-viewer ghost uses it as the rotate
   * gesture's pin-centre anchor (the dot's box centre = the pin point), so the
   * ref must land on the rendered node. See [[pin-marker-dot.spec]].
   */
  ref?: Ref<HTMLDivElement>;
}

/**
 * The shared **pin marker dot** — the solid round marker that says "the pin is
 * here," anchored at the pin point. One component for every editing/placement
 * surface (plan-viewer ghost pin, the fullscreen expanded-plan-view editor, and
 * the image-viewer-modal plan thumbnail) so the marker looks identical
 * everywhere and can't drift into per-surface glyphs (it once diverged into a
 * map-pin teardrop on the record surfaces). See [[directional-pins]].
 *
 * Centres on `(xPct, yPct)` and, by default, rides `--pin-scale` so it holds a
 * constant on-screen size at any plan zoom — matching the view cone and rotate
 * handle. Decorative (`pointer-events: none`) so taps fall through to the
 * surface beneath; the rotate handle, not the dot, owns pointer events.
 */
export function PinMarkerDot({
  xPct,
  yPct,
  size = DEFAULT_SIZE_PX,
  scaleWithZoom = true,
  className,
  ref,
}: PinMarkerDotProps) {
  return (
    <div
      ref={ref}
      data-slot="pin-marker-dot"
      aria-hidden
      className={cn(
        "pointer-events-none absolute rounded-full border-2 border-white bg-tertiary shadow-md",
        className,
      )}
      style={{
        left: `${xPct * 100}%`,
        top: `${yPct * 100}%`,
        width: size,
        height: size,
        // Centre on the pin, then (optionally) ride --pin-scale so the dot
        // holds a constant on-screen size as the plan zooms — pivoting about
        // its own centre, which sits exactly on the pin point.
        transform: `translate(-50%, -50%)${scaleWithZoom ? " scale(var(--pin-scale, 1))" : ""}`,
        transformOrigin: "center center",
      }}
    />
  );
}
